// src/components/ProtectedRoute.jsx
import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { getMe, getCurrentUser, logoutUser } from '@/lib/api';
import { dbg } from '@/lib/logger';

const Loader = () => (
  <div className="flex items-center justify-center py-16">
    <div className="animate-spin h-6 w-6 rounded-full border-2 border-primary border-t-transparent" />
  </div>
);

/**
 * ProtectedRoute
 * - Vérifie la validité du token JWT via /auth/me
 * - Si adminOnly = true, vérifie que role === 'admin'
 * - Si le token est expiré ou absent → redirige vers /login
 */
const ProtectedRoute = ({ children, adminOnly = false, redirectUnauthenticatedTo = '/login' }) => {
  const location = useLocation();
  const [state, setState] = useState({ loading: true, allowed: false });
  const [attemptedFetch, setAttemptedFetch] = useState(false);
  const [authVersion, setAuthVersion] = useState(0);

  useEffect(() => {
    const handler = () => setAuthVersion((v) => v + 1);
    window.addEventListener('authChange', handler);
    window.addEventListener('storage', handler);
    return () => {
      window.removeEventListener('authChange', handler);
      window.removeEventListener('storage', handler);
    };
  }, []);

  useEffect(() => {
    let mounted = true;

    async function verifyAuth() {
      const token = localStorage.getItem('tdv_auth_token');
      const localUser = getCurrentUser();

      // If no token, immediate redirect.
      if (!token) {
        setState({ loading: false, allowed: false });
        return;
      }

      // If we have a local user already (from a previous session) allow immediately unless adminOnly and not admin.
      if (localUser) {
        const normalized = localUser?.user ? { ...localUser, ...localUser.user } : localUser;
        const roleCandidate = normalized.role || normalized.role_name || normalized.user?.role || normalized.user?.role_name || 'designer';
        const isAdmin = String(roleCandidate).toLowerCase() === 'admin' || normalized.role_id === 1;
        if (adminOnly && !isAdmin) {
          setState({ loading: false, allowed: false });
        } else {
          setState({ loading: false, allowed: true });
        }
        // Still attempt background enrichment if not done yet.
        if (!attemptedFetch) {
          try {
            setAttemptedFetch(true);
            const me = await getMe();
            dbg('ProtectedRoute background getMe:', me);
          } catch (e) {
            console.warn('ProtectedRoute background getMe failed:', e?.message);
          }
        }
        return;
      }

      // No local user but token exists -> fetch /auth/me before deciding.
      try {
        setAttemptedFetch(true);
        const me = await getMe();
        dbg('ProtectedRoute getMe (no local user):', me);
        const normalized = me?.user ?? me ?? null;
        if (!normalized) throw new Error('empty_user');
        const roleCandidate = normalized.role || normalized.role_name || normalized.user?.role || normalized.user?.role_name || 'designer';
        const isAdmin = String(roleCandidate).toLowerCase() === 'admin' || normalized.role_id === 1;
        if (adminOnly && !isAdmin) {
          setState({ loading: false, allowed: false });
        } else {
          setState({ loading: false, allowed: true });
        }
      } catch (err) {
        console.error('ProtectedRoute error (fetch auth):', err);
        logoutUser();
        if (mounted) setState({ loading: false, allowed: false });
      }
    }

    verifyAuth();

    return () => {
      mounted = false;
    };
  }, [adminOnly, authVersion]);

  if (state.loading) return <Loader />;

  if (!state.allowed) {
    const token = localStorage.getItem('tdv_auth_token');
    const to = token ? '/login' : redirectUnauthenticatedTo;
    return <Navigate to={to} state={{ from: location }} replace />;
  }

  return children;
};

export default ProtectedRoute;
